import { useState, useEffect, useRef } from 'react';
import { FiltersQueryParamsStates, TalentSearchQueryParams } from '@type/filters';

/** Props */
interface FiltersSimilarSearchProps extends Pick<FiltersQueryParamsStates<TalentSearchQueryParams>, 'queryParams'> {
  isEmpty?: boolean,
}

/**
 * Returns `similarSearchActive=true` when results are empty and the user has active filters.
 * `similarSearch` is never written into `queryParams` — pass it directly to the SWR URL at the call site.
 * Deactivates only when `queryParams` changes (user changed filters), not when results return,
 * which prevents the activate → results → deactivate → empty → activate oscillation loop.
 */
export const useFiltersSimilarSearch = (props: FiltersSimilarSearchProps) => {
  const { queryParams, isEmpty } = props;
  const [similarSearchActive, setSimilarSearchActive] = useState(false);
  const prevQueryParamsKeyRef = useRef(JSON.stringify(queryParams));

  useEffect(() => {
    const currentKey = JSON.stringify(queryParams);
    const hasFilters = Object.keys(queryParams || {}).length > 0;

    // User changed filters → reset; the next fetch will determine if similar search is needed.
    if (currentKey !== prevQueryParamsKeyRef.current) {
      prevQueryParamsKeyRef.current = currentKey;
      setSimilarSearchActive(false);
      return;
    }

    // Activate when current filters yield no results.
    if (isEmpty && hasFilters) {
      setSimilarSearchActive(true);
    }
    // Do NOT deactivate on isEmpty=false: that would re-trigger the empty→activate cycle.
  }, [isEmpty, queryParams]);

  return { similarSearchActive };
};
